import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { find, first, isEmpty, keys, map, orderBy, size } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { getIntermediateResults } from 'api/experimentResults';
import { JobDetails } from 'components/Pages/Jobs/JobDetails';
import { Permission } from 'interfaces/permissionOption';
import { formatDateTimeForUser } from 'utils/dateTime';
import { exportIntermediateResultParquetUnsignedUrl } from 'utils/exports';
import useUsersById from 'utils/queryHooks/useUsersById';
import { useCasesParams } from 'utils/useCasesParams';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { usePermissions } from 'utils/usePermissions';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';

interface IntermediateResultsDownloadModalProps {
  open: boolean;
  onClose: () => void;
}

const LATEST_ORCHESTRATION_ID_OPTION = 'LATEST';

const IntermediateResultsDownloadModal: React.FC<IntermediateResultsDownloadModalProps> = ({ open, onClose }) => {
  const { labId } = useCurrentLabId();
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const { hasPermission } = usePermissions();
  const canViewUnpublishedResults = hasPermission(Permission.ViewUnpublishedResults);

  const { casesParams } = useCasesParams({});

  const { data: slideToOrchestrationArtifacts, isLoading } = useQueryWithErrorAndRetrySnackbar(
    ['intermediateResultsArtifacts', casesParams],
    ({ signal }) => getIntermediateResults(casesParams, signal),
    {
      enabled: open && canViewUnpublishedResults,
    }
  );

  const { getUserNameById } = useUsersById({
    enabled: open && !isEmpty(slideToOrchestrationArtifacts),
  });

  const orderedOrchestrationIds = orderBy(
    keys(slideToOrchestrationArtifacts),
    (orchestrationId) => slideToOrchestrationArtifacts[orchestrationId].startedAt,
    'desc'
  );

  const orchestrationIdOptions = [
    {
      label: 'Latest orchestration',
      value: LATEST_ORCHESTRATION_ID_OPTION,
      orchestrationId: first(orderedOrchestrationIds),
      job: first(orderedOrchestrationIds) ? slideToOrchestrationArtifacts[first(orderedOrchestrationIds)!] : undefined,
    },
    ...map(orderedOrchestrationIds, (orchestrationId) => {
      const job = slideToOrchestrationArtifacts[orchestrationId];
      return {
        label: `${formatDateTimeForUser(job.startedAt)} - ${
          job.userId && canViewUnpublishedResults ? `Run by ${getUserNameById(job.userId) || 'Unknown User'} - ` : ''
        }${job.name || job.id}`,
        value: job.id,
        job,
      };
    }),
  ];

  const [selectedOrchestrationIdOption, setSelectedOrchestrationIdOption] =
    React.useState<string>(LATEST_ORCHESTRATION_ID_OPTION);

  const selectedOrchestrationOption = find(
    orchestrationIdOptions,
    (option) => option?.value === (selectedOrchestrationIdOption || LATEST_ORCHESTRATION_ID_OPTION)
  );

  const jobId = selectedOrchestrationOption?.job?.id;

  return (
    canViewUnpublishedResults && (
      <Dialog
        open={open}
        onClose={(event: any) => {
          event?.stopPropagation?.();
          onClose();
        }}
        onClick={(event) => event.stopPropagation()}
        maxWidth="md"
      >
        <DialogTitle>
          Download Intermediate Results
          {isLoading && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
        </DialogTitle>
        <DialogContent dividers>
          <InputLabel>
            Select the job results to export
            {isLoading ? ` (Loading...)` : ` (${size(slideToOrchestrationArtifacts)} jobs found)`}
          </InputLabel>
          <Select
            value={isLoading ? '' : selectedOrchestrationIdOption || LATEST_ORCHESTRATION_ID_OPTION}
            onChange={(event) => setSelectedOrchestrationIdOption(event.target.value as string)}
            disabled={isLoading || isEmpty(orchestrationIdOptions)}
          >
            {map(orchestrationIdOptions, (option) => (
              <MenuItem key={option?.value} value={option?.value}>
                <ListItemText primary={option?.label} />
              </MenuItem>
            ))}
          </Select>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h3">Job Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="column" spacing={2}>
                <Grid item>
                  <Typography variant="subtitle1" gutterBottom>
                    Artifact URLs
                  </Typography>
                </Grid>
                <Grid item>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Slide ID</TableCell>
                        <TableCell>Experiment Result ID</TableCell>
                        <TableCell>Artifact Type</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <td colSpan={3}>Loading...</td>
                        </TableRow>
                      ) : selectedOrchestrationOption?.job ? (
                        !isEmpty(selectedOrchestrationOption.job.intermediateResults) ? (
                          map(selectedOrchestrationOption.job.intermediateResults, (result) => {
                            return (
                              <TableRow key={`${result.slideId}-${result.experimentResultId}`}>
                                <TableCell>
                                  <Link
                                    to={getUrlToSlidePage({
                                      slideId: result.slideId,
                                      labId,
                                      caseStudyId: selectedOrchestrationOption.job.studyId,
                                    })}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    component={RouterLink}
                                  >
                                    <Typography>{result.slideId}</Typography>
                                  </Link>
                                </TableCell>
                                <TableCell>{result.experimentResultId}</TableCell>
                                <TableCell>{result.artifactType}</TableCell>
                                <TableCell>
                                  <IconButton
                                    title="Download Artifact"
                                    onClick={() => {
                                      exportIntermediateResultParquetUnsignedUrl({
                                        studyId: selectedOrchestrationOption.job?.studyId,
                                        slideId: result.slideId,
                                        orchestrationId: selectedOrchestrationOption.job?.orchestrationId,
                                        fileName: result.fileName,
                                        unsignedUrl: result.storageUrl,
                                      });
                                    }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4}>No intermediate results found</TableCell>
                          </TableRow>
                        )
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4}>No job selected</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>

                {Boolean(selectedOrchestrationOption?.job) && (
                  <Grid item>
                    {isLoading ? (
                      <Typography variant="body1">Loading...</Typography>
                    ) : selectedOrchestrationOption?.job ? (
                      <JobDetails job={selectedOrchestrationOption?.job} />
                    ) : (
                      <Typography variant="body1">No job details available</Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>Cancel</Button>
          <Tooltip title="Bulk download of intermediate results is not yet supported">
            <span>
              <Button
                variant="contained"
                onClick={() => {
                  // downloadIntermediateResultsMutation.mutate();
                }}
                disabled={
                  !jobId || true // Disable for now, until bulk download is supported
                  // || isRunningMutations
                }
              >
                Download Selected Job Results
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    )
  );
};

export default IntermediateResultsDownloadModal;
